import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import {
  deleteFinancing,
  fetchFinancing,
  homeExpenseQueryKeys,
  openStorageDocumentInNewTab,
} from "@apps/dashboard/lib/home-expense-api";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { formatShort } from "@project/ui";
import { parseFinancingSummaryPayload } from "@project/core";
import { FinancingTermProgressBlock } from "@apps/dashboard/components/financings/FinancingTermProgressBlock";

function aiStatusLabel(t: (k: string) => string, status: string): string {
  switch (status) {
    case "skipped":
      return t("dashboardUi.financings.aiSkipped");
    case "pending":
      return t("dashboardUi.financings.aiPending");
    case "processing":
      return t("dashboardUi.financings.aiProcessing");
    case "completed":
      return t("dashboardUi.financings.aiCompleted");
    case "failed":
      return t("dashboardUi.financings.aiFailed");
    default:
      return status;
  }
}

export default function FinancingDetailPage(): JSX.Element {
  const { houseId, financingId } = useParams<{ houseId: string; financingId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEditHouse = actorRole === "owner" || actorRole === "admin";
  const [deleteError, setDeleteError] = useState<string | null>(null);
  useSyncHouseFromParams(houseId);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !financingId) throw new Error("missing");
      return deleteFinancing(locale, houseId, financingId);
    },
    onSuccess: async () => {
      if (!houseId) return;
      setDeleteError(null);
      await queryClient.invalidateQueries({
        queryKey: homeExpenseQueryKeys.financingsList(locale, houseId),
      });
      navigate(dashboardPaths.houseFinancings(houseId));
    },
    onError: (e: unknown) => {
      if (e instanceof ApiRequestError) {
        setDeleteError(e.message);
        return;
      }
      setDeleteError(t("dashboardUi.states.errorHint"));
    },
  });

  const detailQuery = useQuery({
    queryKey:
      houseId && financingId
        ? homeExpenseQueryKeys.financingDetail(locale, houseId, financingId)
        : ["fin", "d"],
    queryFn: () => {
      if (!houseId || !financingId) throw new Error("missing");
      return fetchFinancing(locale, houseId, financingId);
    },
    enabled: Boolean(houseId && financingId),
  });

  if (!houseId || !financingId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  const row = detailQuery.data;
  const summary =
    row?.aiSummaryStatus === "completed" && row.aiSummaryPayload
      ? parseFinancingSummaryPayload(row.aiSummaryPayload)
      : null;

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houseFinancings(houseId)}>
        {t("dashboardUi.financings.pageTitle")}
      </DashboardBackLink>

      {deleteError ? (
        <p className="mt-4 rounded-xl bg-error-container/30 px-4 py-3 text-body-sm text-error" role="alert">
          {deleteError}
        </p>
      ) : null}

      {detailQuery.isLoading ? (
        <div className="mt-8">
          <LoadingState />
        </div>
      ) : detailQuery.isError || !row ? (
        <div className="mt-8">
          <ErrorState />
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-label-sm uppercase tracking-widest text-on-surface-variant">
                {t("dashboardUi.pages.financingDetail")}
              </p>
              <h1 className="mt-1 font-headline text-headline-sm font-bold leading-snug tracking-tight text-primary">
                {row.title}
              </h1>
              {row.lender ? (
                <p className="mt-1.5 text-body-sm font-normal text-on-surface-variant">
                  <span className="sr-only">{t("dashboardUi.financings.lenderLabel")}: </span>
                  {row.lender}
                </p>
              ) : null}
            </div>
            {canEditHouse ? (
              <div className="flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={dashboardPaths.houseFinancingEdit(houseId, financingId)}>
                    {t("dashboardUi.financings.editCta")}
                  </Link>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-error/40 text-error hover:bg-error/10"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    setDeleteError(null);
                    if (window.confirm(t("dashboardUi.financings.deleteConfirm"))) {
                      void deleteMutation.mutateAsync();
                    }
                  }}
                >
                  {t("dashboardUi.financings.deleteCta")}
                </Button>
              </div>
            ) : null}
          </header>

          <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5">
            <p className="text-label-md text-on-surface-variant">
              {t("dashboardUi.financings.listInstallment", {
                amount: formatMinorAsCurrency(row.installmentAmountMinor, row.currency, locale),
              })}
            </p>
            <p className="mt-2 text-body-md text-on-surface">
              {formatShort(row.contractStartOn)} → {formatShort(row.endsOn)}
            </p>
            <FinancingTermProgressBlock row={row} className="mt-4" />
            {row.expenseSeriesId ? (
              <Button asChild variant="outline" className="mt-4">
                <Link to={dashboardPaths.houseExpenseSeries(houseId, row.expenseSeriesId)}>
                  {t("dashboardUi.financings.viewSeriesCta")}
                </Link>
              </Button>
            ) : null}
          </section>

          <section>
            <h2 className="font-headline text-title-md text-on-surface">{t("dashboardUi.financings.summaryTitle")}</h2>
            <p className="mt-1 text-body-sm text-on-surface-variant">{aiStatusLabel(t, row.aiSummaryStatus)}</p>
            <p className="mt-2 text-body-xs text-on-surface-variant">{t("dashboardUi.financings.aiDisclaimer")}</p>
            {summary?.summaryBullets?.length ? (
              <ul className="mt-4 list-inside list-disc space-y-1 text-body-md text-on-surface">
                {summary.summaryBullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            ) : null}
            {summary?.keyTerms?.length ? (
              <div className="mt-6">
                <h3 className="font-label text-label-md font-bold text-on-surface">
                  {t("dashboardUi.financings.keyTermsTitle")}
                </h3>
                <dl className="mt-2 space-y-2">
                  {summary.keyTerms.map((kt, i) => (
                    <div key={i}>
                      <dt className="text-label-sm text-on-surface-variant">{kt.label}</dt>
                      <dd className="text-body-md text-on-surface">{kt.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
          </section>

          {row.documents.length > 0 ? (
            <section>
              <h2 className="font-headline text-title-md text-on-surface">
                {t("dashboardUi.vault.documentKeyLabel")}
              </h2>
              <ul className="mt-3 space-y-2">
                {row.documents.map((d) => (
                  <li key={d.id}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void openStorageDocumentInNewTab(locale, d.storageKey)}
                    >
                      {d.fileName?.trim() || t("dashboardUi.financings.openDocument")}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
